"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { formatUsd } from "@/lib/currency";
import { BookDemoButton } from "@/components/ui/book-demo-button";
import { Footer } from "../_components/footer";
import { Header } from "../_components/header";

type TeamValue = 1 | 2 | 3 | 5;

interface TeamOption {
  value: TeamValue;
  label: string;
}

const TEAM_OPTIONS: TeamOption[] = [
  { value: 1, label: "Just me" },
  { value: 2, label: "2 people" },
  { value: 3, label: "3-5" },
  { value: 5, label: "5+" },
];

interface SliderSpec {
  id: keyof SliderValues;
  label: string;
  max: number;
  initial: number;
  breakdownLabel: string;
}

interface SliderValues {
  email: number;
  lead: number;
  admin: number;
  sched: number;
}

const SLIDERS: SliderSpec[] = [
  {
    id: "email",
    label: "Email & follow-up work",
    max: 40,
    initial: 8,
    breakdownLabel: "Email & follow-ups automated",
  },
  {
    id: "lead",
    label: "Lead follow-up & outreach",
    max: 40,
    initial: 6,
    breakdownLabel: "Lead outreach automated",
  },
  {
    id: "admin",
    label: "Admin, reporting & data entry",
    max: 30,
    initial: 4,
    breakdownLabel: "Admin & reporting automated",
  },
  {
    id: "sched",
    label: "Scheduling & calendar management",
    max: 20,
    initial: 3,
    breakdownLabel: "Scheduling automated",
  },
];

// There is no public price to subtract: every client gets a custom plan,
// quoted after an audit call. So this shows only the value of the time
// the automation would free up, never a "net savings" figure built on a
// made-up Raileon cost. The CTA sends visitors to the audit call for the
// real comparison.
//
// Ported from raileon-roi-calculator.html. The original used
// imperative DOM manipulation (innerText / style.width) in a calc()
// function fired on every input. Here the same math lives in a
// useMemo over React state — cleaner and survives React Strict
// Mode's double-rendering without double-registering listeners.
export function RoiContent() {
  const [values, setValues] = useState<SliderValues>(() => ({
    email: SLIDERS[0]!.initial,
    lead: SLIDERS[1]!.initial,
    admin: SLIDERS[2]!.initial,
    sched: SLIDERS[3]!.initial,
  }));
  const [hourlyRate, setHourlyRate] = useState<number>(75);
  const [teamSize, setTeamSize] = useState<TeamValue>(1);
  // Conservative default (was a hardcoded, unsourced 85%) — #1074's
  // recommended default (pending founder sign-off) is to expose it as a
  // user-adjustable slider rather than assert a rate nobody measured.
  const [automationRate, setAutomationRate] = useState<number>(0.5);

  // Mirror of the original calc() function — everything downstream
  // is a derivation of the four sliders + hourly rate + team size +
  // automation rate.
  const results = useMemo(() => {
    const rate = Number.isFinite(hourlyRate) && hourlyRate > 0 ? hourlyRate : 50;
    const { email, lead, admin, sched } = values;

    const totalHoursWeek = (email + lead + admin + sched) * teamSize;
    const monthlyHours = totalHoursWeek * 4.33;
    const currentMonthlyCost = monthlyHours * rate;

    const hoursSaved = totalHoursWeek * automationRate;
    const monthlySaved = hoursSaved * 4.33 * rate;
    const yearlySaved = monthlySaved * 12;

    // Per-category monthly savings for the breakdown panel.
    const breakdown = {
      email: email * teamSize * 4.33 * rate * automationRate,
      lead: lead * teamSize * 4.33 * rate * automationRate,
      admin: admin * teamSize * 4.33 * rate * automationRate,
      sched: sched * teamSize * 4.33 * rate * automationRate,
    };

    // Comparison-bar widths. The time spent today always fills (100%);
    // the freed-up share scales to the same reference, min 3% so a zero
    // value still shows a nub.
    const freedBarPct = Math.max(
      3,
      currentMonthlyCost > 0 ? (monthlySaved / currentMonthlyCost) * 100 : 3,
    );

    return {
      totalHoursWeek,
      currentMonthlyCost,
      hoursSaved,
      monthlySaved,
      yearlySaved,
      breakdown,
      freedBarPct,
    };
  }, [values, hourlyRate, teamSize, automationRate]);

  return (
    <main id="main">
      <Header />
      <div className="raileon-roi">
        <div className="calc-section">
          <div className="section-tag">ROI Calculator</div>
          <h1 className="section-title">
            How Much Could You Save
            <br />
            With AI Automation?
          </h1>
          <div className="section-sub">
            Drag the sliders to match your business. See the impact in real
            time.
          </div>

          <div className="calc-layout">
            {/* LEFT: INPUTS */}
            <div className="calc-inputs">
              <div className="calc-inputs-title">Your Business Today</div>
              <div className="calc-inputs-sub">
                Adjust each slider to match your current workload.
              </div>

              {SLIDERS.map((s) => (
                <SliderInput
                  key={s.id}
                  spec={s}
                  value={values[s.id]}
                  onChange={(next) =>
                    setValues((prev) => ({ ...prev, [s.id]: next }))
                  }
                />
              ))}

              <div className="divider" />

              {/* Hourly rate */}
              <div className="input-group">
                <div className="input-label">
                  <span className="input-label-text">
                    What you value your time at ($/hour)
                  </span>
                </div>
                <div className="cost-input-wrap">
                  <span className="dollar">$</span>
                  <input
                    type="number"
                    className="cost-input"
                    aria-label="Hourly rate in dollars"
                    min={15}
                    max={500}
                    value={hourlyRate}
                    onChange={(e) => {
                      const parsed = parseFloat(e.target.value);
                      setHourlyRate(
                        Number.isFinite(parsed) ? parsed : 0,
                      );
                    }}
                  />
                </div>
              </div>

              {/* Team size */}
              <div className="input-group">
                <div className="input-label">
                  <span className="input-label-text">
                    Team size doing this work
                  </span>
                </div>
                <div className="team-select">
                  {TEAM_OPTIONS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      className={`team-btn${
                        teamSize === t.value ? " active" : ""
                      }`}
                      onClick={() => setTeamSize(t.value)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Automation rate — no fixed constant exists for this, so
                  it's user-adjustable with a conservative default rather
                  than an asserted, unsourced percentage. */}
              <div className="input-group">
                <div className="input-label">
                  <span className="input-label-text">
                    Estimated automation rate
                  </span>
                  <span className="input-label-value">
                    {Math.round(automationRate * 100)}%
                  </span>
                </div>
                <div className="slider-wrap">
                  <input
                    type="range"
                    aria-label="Estimated automation rate"
                    min={10}
                    max={90}
                    step={5}
                    value={Math.round(automationRate * 100)}
                    onChange={(e) =>
                      setAutomationRate(parseInt(e.target.value, 10) / 100)
                    }
                  />
                </div>
              </div>
            </div>

            {/* RIGHT: RESULTS */}
            <div className="calc-results">
              <div className="results-card">
                <div className="results-title">What Automation Frees Up</div>

                <div className="big-stat">
                  <div className="big-stat-label">
                    Value of the time you&apos;d free up
                  </div>
                  <div className="big-stat-value">
                    {formatUsd(Math.max(0, Math.round(results.monthlySaved)))}
                  </div>
                  <div className="big-stat-sub">
                    Per month, or{" "}
                    {formatUsd(Math.max(0, Math.round(results.yearlySaved)))} a
                    year
                  </div>
                </div>

                {/* Visible without scrolling past the result — the
                    number above is an estimate built on the assumptions
                    listed here, not a quote. */}
                <div className="assumptions-block">
                  <strong>Estimate, not a quote.</strong> Assumes{" "}
                  {formatUsd(
                    Number.isFinite(hourlyRate) && hourlyRate > 0
                      ? hourlyRate
                      : 50,
                  )}
                  /hour and {Math.round(automationRate * 100)}% of the hours
                  above automated. This is the value of the time freed up,
                  not what Raileon costs: every plan is custom, and we put
                  real numbers side by side in your free audit call. Excludes
                  social media posting, which Raileon doesn&apos;t automate
                  yet.
                </div>

                <div className="stat-grid">
                  <div className="stat-box highlight">
                    <div className="stat-box-value">
                      {Math.round(results.hoursSaved)}
                    </div>
                    <div className="stat-box-label">Hours saved / week</div>
                  </div>
                  <div className="stat-box highlight">
                    <div className="stat-box-value">
                      {(results.hoursSaved / 8).toFixed(1)}
                    </div>
                    <div className="stat-box-label">Days freed up / week</div>
                  </div>
                </div>

                {/* Comparison bars */}
                <div className="compare-section">
                  <div className="compare-label">
                    <span>Your time on these tasks today, per month</span>
                    <span>
                      {formatUsd(Math.round(results.currentMonthlyCost))}
                    </span>
                  </div>
                  <div className="compare-bar">
                    <div
                      className="compare-fill current"
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div className="compare-label">
                    <span>Time freed up, per month</span>
                    <span>
                      {formatUsd(Math.round(results.monthlySaved))}
                    </span>
                  </div>
                  <div className="compare-bar">
                    <div
                      className="compare-fill raileon"
                      style={{ width: `${results.freedBarPct}%` }}
                    />
                  </div>
                </div>

                {/* Breakdown */}
                <div className="breakdown">
                  <div className="breakdown-header">Monthly Breakdown</div>
                  {SLIDERS.map((s) => (
                    <div className="breakdown-row" key={s.id}>
                      <span className="bl">{s.breakdownLabel}</span>
                      <span className="bv">
                        {formatUsd(Math.round(results.breakdown[s.id]))}
                      </span>
                    </div>
                  ))}
                  <div className="breakdown-row total">
                    <span className="bl">Time freed up, per month</span>
                    <span className="bv">
                      {formatUsd(
                        Math.max(0, Math.round(results.monthlySaved)),
                      )}
                    </span>
                  </div>
                </div>

                <div className="results-cta-wrap">
                  <BookDemoButton>
                    Get your exact numbers in a free audit call
                  </BookDemoButton>
                </div>
                <div className="results-note">
                  No commitment. No credit card. Just a conversation.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{roiCss}</style>
      <Footer />
    </main>
  );
}

// Per-slider component. The original HTML painted a dynamic
// linear-gradient over the range input to show the "fill" portion —
// we replicate that by computing the percentage in render + applying
// it to the slider's background via the style attr.
function SliderInput({
  spec,
  value,
  onChange,
}: {
  spec: SliderSpec;
  value: number;
  onChange: (next: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-paint the slider fill whenever the value changes. React's
  // style attr handles this declaratively — no JS measurement needed.
  useEffect(() => {
    if (!inputRef.current) return;
    const pct = (value / spec.max) * 100;
    inputRef.current.style.background = `linear-gradient(to right, #10B981 0%, #10B981 ${pct}%, #1A1D2B ${pct}%, #1A1D2B 100%)`;
  }, [value, spec.max]);

  return (
    <div className="input-group">
      <div className="input-label">
        <span className="input-label-text">{spec.label}</span>
        <span className="input-label-value">{value} hrs/week</span>
      </div>
      <div className="slider-wrap">
        <input
          ref={inputRef}
          type="range"
          aria-label={spec.label}
          min={0}
          max={spec.max}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
        />
      </div>
    </div>
  );
}

const roiCss = `
.raileon-roi { background: #04050A; color: #F1F3F5; min-height: 100vh; }
.raileon-roi * { box-sizing: border-box; }

.raileon-roi .calc-section {
  max-width: 1080px;
  margin: 0 auto;
  padding: 80px 24px 100px;
}

.raileon-roi .section-tag {
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #10B981;
  letter-spacing: 4px;
  text-transform: uppercase;
  margin-bottom: 14px;
}
.raileon-roi .section-title {
  text-align: center;
  font-size: clamp(26px, 4.5vw, 40px);
  font-weight: 900;
  line-height: 1.15;
  margin-bottom: 10px;
}
.raileon-roi .section-sub {
  text-align: center;
  font-size: 15px;
  color: #8B8FA3;
  margin-bottom: 50px;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
}

.raileon-roi .calc-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: start;
}

.raileon-roi .calc-inputs {
  background: #0A0C14;
  border: 1px solid #1A1D2B;
  border-radius: 16px;
  padding: 32px;
}
.raileon-roi .calc-inputs-title {
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 4px;
}
.raileon-roi .calc-inputs-sub {
  font-size: 12px;
  color: #474B5F;
  margin-bottom: 28px;
}

.raileon-roi .input-group { margin-bottom: 22px; }
.raileon-roi .input-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.raileon-roi .input-label-text {
  font-size: 13px;
  font-weight: 600;
  color: #8B8FA3;
}
.raileon-roi .input-label-value {
  font-size: 14px;
  font-weight: 700;
  color: #F1F3F5;
  font-family: var(--font-mono), 'JetBrains Mono', monospace;
}

.raileon-roi .slider-wrap {
  position: relative;
  height: 36px;
  display: flex;
  align-items: center;
}
.raileon-roi input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 6px;
  background: #1A1D2B;
  outline: none;
  cursor: pointer;
}
.raileon-roi input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #10B981;
  border: 3px solid #0A0C14;
  box-shadow: 0 0 12px rgba(16,185,129,0.4);
  cursor: pointer;
  transition: transform 0.15s ease;
}
.raileon-roi input[type="range"]::-webkit-slider-thumb:hover {
  transform: scale(1.15);
}
.raileon-roi input[type="range"]::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #10B981;
  border: 3px solid #0A0C14;
  box-shadow: 0 0 12px rgba(16,185,129,0.4);
  cursor: pointer;
}

.raileon-roi .cost-input-wrap { position: relative; }
.raileon-roi .cost-input-wrap .dollar {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 15px;
  font-weight: 700;
  color: #474B5F;
  font-family: var(--font-mono), 'JetBrains Mono', monospace;
}
.raileon-roi .cost-input {
  width: 100%;
  padding: 14px 14px 14px 28px;
  background: #0E1019;
  border: 1px solid #1A1D2B;
  border-radius: 10px;
  color: #F1F3F5;
  font-size: 15px;
  font-weight: 600;
  font-family: var(--font-mono), 'JetBrains Mono', monospace;
  outline: none;
  transition: border-color 0.2s;
}
.raileon-roi .cost-input:focus { border-color: #10B981; }

.raileon-roi .team-select {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.raileon-roi .team-btn {
  padding: 10px 8px;
  background: #0E1019;
  border: 2px solid #1A1D2B;
  border-radius: 8px;
  color: #8B8FA3;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
  font: inherit;
  font-weight: 600;
}
.raileon-roi .team-btn:hover {
  border-color: #474B5F;
  color: #F1F3F5;
}
.raileon-roi .team-btn.active {
  border-color: #10B981;
  background: rgba(16,185,129,0.06);
  color: #10B981;
}

.raileon-roi .divider {
  height: 1px;
  background: #1A1D2B;
  margin: 24px 0;
}

.raileon-roi .calc-results {
  position: sticky;
  top: 100px;
}

.raileon-roi .results-card {
  background: #0A0C14;
  border: 1px solid #1A1D2B;
  border-radius: 16px;
  padding: 32px;
  position: relative;
  overflow: hidden;
}
.raileon-roi .results-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #10B981, #06B6D4);
}

.raileon-roi .results-title {
  font-size: 14px;
  font-weight: 700;
  color: #474B5F;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 24px;
}

.raileon-roi .big-stat { text-align: center; margin-bottom: 28px; }
.raileon-roi .big-stat-label {
  font-size: 12px;
  font-weight: 600;
  color: #474B5F;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
}
.raileon-roi .big-stat-value {
  font-size: 48px;
  font-weight: 900;
  font-family: var(--font-mono), 'JetBrains Mono', monospace;
  color: #10B981;
  line-height: 1;
}
.raileon-roi .big-stat-sub {
  font-size: 13px;
  color: #8B8FA3;
  margin-top: 6px;
}

.raileon-roi .stat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
}
.raileon-roi .stat-box {
  background: #0E1019;
  border: 1px solid #1A1D2B;
  border-radius: 10px;
  padding: 16px;
  text-align: center;
}
.raileon-roi .stat-box-value {
  font-size: 22px;
  font-weight: 800;
  font-family: var(--font-mono), 'JetBrains Mono', monospace;
  color: #F1F3F5;
  line-height: 1;
  margin-bottom: 4px;
}
.raileon-roi .stat-box-label {
  font-size: 10px;
  font-weight: 600;
  color: #474B5F;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.raileon-roi .stat-box.highlight .stat-box-value { color: #10B981; }

.raileon-roi .compare-section { margin-bottom: 24px; }
.raileon-roi .compare-label {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 6px;
}
.raileon-roi .compare-label span:first-child { color: #8B8FA3; }
.raileon-roi .compare-label span:last-child {
  color: #F1F3F5;
  font-family: var(--font-mono), 'JetBrains Mono', monospace;
}
.raileon-roi .compare-bar {
  height: 8px;
  background: #0E1019;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
}
.raileon-roi .compare-fill {
  height: 100%;
  border-radius: 8px;
  transition: width 0.6s ease;
}
.raileon-roi .compare-fill.current { background: #EF4444; }
.raileon-roi .compare-fill.raileon {
  background: #10B981;
  box-shadow: 0 0 12px rgba(16,185,129,0.3);
}

.raileon-roi .results-cta-wrap {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.raileon-roi .assumptions-block {
  background: #0E1019;
  border: 1px solid #1A1D2B;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 11.5px;
  line-height: 1.6;
  color: #8B8FA3;
  margin-bottom: 20px;
}
.raileon-roi .assumptions-block strong {
  color: #F1F3F5;
}

.raileon-roi .results-note {
  text-align: center;
  font-size: 11px;
  color: #474B5F;
  margin-top: 12px;
}

.raileon-roi .breakdown {
  margin-top: 20px;
  background: #0E1019;
  border: 1px solid #1A1D2B;
  border-radius: 12px;
  overflow: hidden;
}
.raileon-roi .breakdown-header {
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 700;
  color: #474B5F;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid #1A1D2B;
}
.raileon-roi .breakdown-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 16px;
  font-size: 12px;
  border-bottom: 1px solid rgba(26,29,43,0.5);
}
.raileon-roi .breakdown-row:last-child { border-bottom: none; }
.raileon-roi .breakdown-row .bl { color: #8B8FA3; }
.raileon-roi .breakdown-row .bv {
  color: #F1F3F5;
  font-weight: 600;
  font-family: var(--font-mono), 'JetBrains Mono', monospace;
}
.raileon-roi .breakdown-row.total { background: rgba(16,185,129,0.06); }
.raileon-roi .breakdown-row.total .bl {
  color: #10B981;
  font-weight: 700;
}
.raileon-roi .breakdown-row.total .bv {
  color: #10B981;
  font-weight: 800;
}

@media (max-width: 768px) {
  .raileon-roi .calc-layout { grid-template-columns: 1fr; }
  .raileon-roi .calc-results { position: static; }
  .raileon-roi .calc-section { padding: 50px 16px 60px; }
  .raileon-roi .calc-inputs,
  .raileon-roi .results-card { padding: 24px; }
  .raileon-roi .big-stat-value { font-size: 36px; }
  .raileon-roi .team-select { grid-template-columns: repeat(2, 1fr); }
}
`;
